import { useState } from 'react';
import type { MapObject } from '@etown/shared';
import { Modal } from '../../components/Modal';
import { adminApi } from '../../net/adminApi';
import { useOffice } from '../office/OfficeContext';
import './objects.css';

/** YouTube-Video-ID aus einer URL (oder roher ID) extrahieren. */
function parseVideoId(input: string): string | null {
  const trimmed = input.trim();
  if (/^[\w-]{11}$/.test(trimmed)) return trimmed; // rohe ID
  try {
    const url = new URL(trimmed);
    if (url.hostname === 'youtu.be') return url.pathname.slice(1, 12) || null;
    if (url.searchParams.get('v')) return url.searchParams.get('v')!.slice(0, 11);
    const m = url.pathname.match(/\/(embed|shorts)\/([\w-]{11})/);
    if (m) return m[2]!;
  } catch {
    // keine URL
  }
  return null;
}

/** Eingebettetes YouTube-Video; Admins koennen den Link aendern (einfach pasten). */
export function YouTubeModal({ object, onClose }: { object: MapObject; onClose: () => void }) {
  const { mapId, istAdmin: isAdmin } = useOffice();
  const [videoId, setVideoId] = useState(object.props.videoId ?? '');
  const [input, setInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async (): Promise<void> => {
    const parsed = parseVideoId(input);
    if (!parsed) {
      setError('Kein gültiger YouTube-Link.');
      return;
    }
    setError(null);
    setSaving(true);
    try {
      await adminApi.setObjectProps(mapId, object.id, { videoId: parsed });
      setVideoId(parsed); // sofort im Player uebernehmen (map:updated folgt fuer alle)
      setInput('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Video" onClose={onClose} wide>
      {videoId ? (
        <div className="youtube__frame-wrap">
          <iframe
            className="youtube__frame"
            src={`https://www.youtube-nocookie.com/embed/${encodeURIComponent(videoId)}`}
            title="YouTube-Video"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            loading="lazy"
          />
        </div>
      ) : (
        <p>Für dieses Objekt ist kein Video hinterlegt.</p>
      )}
      {isAdmin && (
        <div className="youtube__admin">
          <input
            type="text"
            className="youtube__link-input"
            placeholder="YouTube-Link einfügen…"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void save();
            }}
          />
          <button className="youtube__link-save" onClick={() => void save()} disabled={saving || !input.trim()}>
            {saving ? 'Speichert…' : 'Link setzen'}
          </button>
          {error && <span className="youtube__link-error" role="alert">{error}</span>}
        </div>
      )}
    </Modal>
  );
}
